import { VoiceComposer } from '@orborix/voice-composer';
import { Underline } from '../../components/doodles/Doodles.js';
import { DrillModal } from './messages/index.js';
import { ConversationMessages } from './ConversationMessages.js';
import { ConnectionBanner } from './ConnectionBanner.js';
import { SessionClosingCard, SessionEndedCard } from './SessionEndedCard.js';
import { ReplyFailedCard } from './ReplyFailedCard.js';
import { SettingsSheet } from './controls/SettingsSheet.js';
import { PrivateBadge } from './controls/PrivateBadge.js';
import { InspireButton, InspireCard } from './controls/Inspire.js';
import { AssistedCard } from './assisted/AssistedCard.js';
import { AssistedIntroSheet } from './assisted/AssistedIntroSheet.js';
import { A0Suggestions } from './controls/A0Suggestions.js';
import { DirectionMenu } from './controls/SeedMenu.js';
import { SecretWordCard } from './controls/SecretWordCard.js';
import { GameModeChip } from './controls/GameModeChip.js';
import { TapWordHint } from './controls/TapWordHint.js';
import { JumpToLatestChip } from './controls/atoms.js';
import { IconChatGear, IconPlus, IconSpeaker, IconSpeakerOff, IconX } from './icons.js';
import {
  COMPOSER_LABELS,
  COMPOSER_PLACEHOLDER,
  COMPOSER_PLACEHOLDER_ENDED,
  COMPOSER_PLACEHOLDER_TEXT_ONLY,
} from './composerLabels.js';
import { ShortLeaveSheet } from './ShortLeaveSheet.js';
import { FALLBACK_VOICE_ID } from './conversationTypes.js';
import { MissionTracker } from '../scenarios/MissionTracker.js';
import { UnitObjectivesTracker } from './UnitObjectivesTracker.js';
import { MissionFinishFab } from '../scenarios/MissionFinishFab.js';
import { ExamProgress } from './ExamProgress.js';
import { CelebrationLayer } from '../../lib/celebrations/index.js';
import { useSkillCompletionTick } from './hooks/useSkillCompletionTick.js';
import { ConversationListenBar } from '../listen/ConversationListenBar.js';
import { useEffect, useRef, type CSSProperties } from 'react';
import { ConversationCoachmarks } from '../../components/coachmark/ConversationCoachmarks.js';
import type { UseConversationController } from './useConversationController.js';
import {
  ShipPieceTracker,
  conversationLive,
  conversationPiece,
} from '../home/ship/ShipPieceTracker.js';
import { useInspireReadsAloud } from '../home/ship/useShip.js';

interface ConversationMobileProps {
  ctrl: UseConversationController;
}

/**
 * Layout móvil de la conversación: columna única (header → mensajes → mic bar).
 * Shell presentacional puro — toda la lógica vive en `useConversationController`
 * y la lista de mensajes en `ConversationMessages` (ambas compartidas con la
 * variante de escritorio).
 */
export function ConversationMobile({ ctrl }: ConversationMobileProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const {
    settings,
    setSettings,
    setGlobalIntensity,
    coachMuted,
    toggleCoachMuted,
    muteNotice,
    micHidden,
    recorder,
    freeChat,
    assistedPaused,
    freeStatus,
    holdDisabled,
    coach,
    inspire,
    assisted,
    sendComposerText,
    sendInspireSuggestion,
    drillErr,
    setDrillErr,
    settingsOpen,
    setSettingsOpen,
    incognito,
    toggleIncognito,
    threadRef,
    orbRef,
    threadUnpinned,
    handleThreadScroll,
    jumpToLatest,
    state,
    isUserTurn,
    connectionStatus,
    reconnect,
    sessionId,
    pauseMainCapture,
    resumeMainCapture,
    leave,
    requestLeave,
    shortLeavePrompt,
    confirmLeaveShort,
    dismissLeaveShort,
    scenarioMission,
    completedObjectives,
    justObjectiveId,
    practicedObjectives,
    justPracticedId,
    allObjectivesDone,
    examMode,
    messages,
    planLabel,
    planObjectives,
    completedPlanObjectives,
    justPlanObjectiveIdx,
    allPlanObjectivesDone,
    subUnit,
    subUnitObjectives,
    subUnitReps,
    subUnitCounts,
    subUnitCompleted,
    subUnitReview,
    justSubObjIdx,
    subRepTick,
    seedMenuOpen,
    activeSeed,
    seedDisabledReason,
    openSeedMenu,
    closeSeedMenu,
    selectSeed,
    clearSeed,
    selectNews,
    goToScenarios,
    secretWord,
    rerollSecretWord,
    a0Mission,
    a0SuggestionStep,
    sendA0Suggestion,
    a0CelebrateTick,
    serverEnded,
    serverClosing,
    resumeConversation,
    replyFailure,
    retryFailedReply,
    gameArena,
  } = ctrl;
  const shipPiece = conversationPiece({
    examMode,
    a0Mission: ctrl.a0Mission,
    scenarioLabel: ctrl.scenarioLabel,
    planLabel: ctrl.planLabel,
    activeSeed: ctrl.shipSeed,
  });
  // La Cabina desde A1: la frase de «Inspirar» queda a la vista hasta que el alumno habla (card #603).
  const inspireReadAloud = useInspireReadsAloud(shipPiece);
  const { close: closeInspire } = inspire;
  useEffect(() => {
    if (inspireReadAloud && state === 'thinking') closeInspire();
  }, [inspireReadAloud, state, closeInspire]);
  // A skill completed in free talk (W6): small sparkles, no sound (D7).
  const skillCompleteTick = useSkillCompletionTick(ctrl.messages);
  const ended = serverEnded != null || ctrl.lessonCompletion?.phase === 'closing' || ctrl.lessonCompletion?.phase === 'closed';
  // Una vez por parte confirmada, sólo en vivo: un cierre restaurado al
  // reconectar llega `closed` y no se vuelve a festejar.
  const lessonEndTrigger = ctrl.lessonCompletion?.phase === 'closing';

  return (
    <div
      className={`app bubbles-${settings.density} ${ended ? 'conv-ended' : ''} conv-arena-root ${gameArena.seed ? 'is-arena' : ''}`}
      style={gameArena.tint ? ({ '--arena-color': gameArena.tint } as CSSProperties) : undefined}
      ref={rootRef}
    >
      {/* Coach-marks de onboarding contextual (primera charla estándar). */}
      <ConversationCoachmarks containerRef={rootRef} ctrl={ctrl} />
      {/* Festejo al completar TODAS las misiones del escenario. Capa contenida
          al frame (no fullscreen), dispara una sola vez en la transición. */}
      {scenarioMission && (
        <CelebrationLayer type="confetti-cannons" trigger={allObjectivesDone} sound="celebrate-big" />
      )}
      {/* Bonus del escenario cumplido: un escalón sobre el confeti (encargo 104,
          Emmanuel eligió la lluvia dorada). */}
      {scenarioMission && (
        <CelebrationLayer type="bonus-rain" trigger={ctrl.bonusDone} sound="bonus-rain" />
      )}
      {/* Final de clase: el mismo festejo al confirmarse la parte (encargo 104). */}
      {!scenarioMission && subUnit && (
        <CelebrationLayer type="confetti-cannons" trigger={lessonEndTrigger} sound="celebrate-big" />
      )}
      {/* Festejo chico al completar una micro-charla A0 (no-boss). El boss festeja
          aparte con A0Ceremony en su propio flujo de graduación. */}
      {a0Mission && !a0Mission.isBoss && (
        <CelebrationLayer type="sparkles" trigger={a0CelebrateTick} sound="celebrate-small" />
      )}
      {/* Habilidad completada en charla libre (W6): chispas chicas, SIN sonido
          — la charla no se interrumpe; el chip de la burbuja dice cuál. */}
      <CelebrationLayer type="sparkles" trigger={skillCompleteTick} />

      {/* Header. El título es firma, no información: va en susurro (20px,
          muted, sin sparks) — a 30px era el texto más grande de la pantalla y
          competía con la conversación. Sigue anclando el pill del programa y
          balanceando la X contra los controles de la derecha. */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 18px 4px', marginTop: 2 }}>
        <button className="conv-head-ghost" onClick={requestLeave} aria-label="Cerrar">
          <IconX size={20} />
        </button>
        <div ref={orbRef} style={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <span className="script" style={{ fontSize: 20, lineHeight: 1, color: 'var(--text-muted)' }}>
            Conversación
          </span>
          <Underline color="var(--lime)" width={72} style={{ marginTop: 1, opacity: 0.6 }} />
          {planLabel && <span className="conv-plan-pill">Programa · {planLabel.title}</span>}
        </div>
        {/* Variante B aprobada: los controles secundarios (voz del coach |
            ajustes) van agrupados en UNA pill con divisor; la X queda como
            glifo fantasma. Actividades vive en el "+" del composer. */}
        <div className="conv-head-cluster">
          {/* Toggle de voz del coach: altavoz activo = responde con voz;
              tachado (lima) = sólo texto, sin TTS. Cambia EN VIVO. */}
          <button
            className={`conv-cluster-btn conv-voice-toggle ${coachMuted ? 'is-muted' : ''}`}
            aria-label={coachMuted ? 'Activar la voz del coach' : 'Silenciar la voz del coach'}
            aria-pressed={coachMuted}
            disabled={ended}
            onClick={toggleCoachMuted}
          >
            {coachMuted ? <IconSpeakerOff size={17} /> : <IconSpeaker size={17} />}
          </button>
          <span className="conv-cluster-div" aria-hidden />
          <button
            className={`conv-cluster-btn ${incognito ? 'has-private-dot' : ''}`}
            aria-label="Ajustes de la charla"
            disabled={ended}
            onClick={() => {
              inspire.close();
              setSettingsOpen(true);
            }}
          >
            <IconChatGear size={17} />
          </button>
        </div>
      </div>

      {/* Toast efímero del mute (5s, fade vía CSS): al silenciar y al abrir
          una charla ya muteada. El ancla de altura 0 no empuja el layout. */}
      {muteNotice && (
        <div className="conv-mute-toast-anchor">
          <div className="conv-mute-toast" role="status">
            <IconSpeakerOff size={12} />
            <span>El coach responde por texto (gastás menos orbs)</span>
          </div>
        </div>
      )}

      {/* Mini-player de "Escuchar" (19): barra persistente JUSTO debajo del
          header, si hay una sesión de escucha viva y el user no la cerró.
          Comparte el <audio> único del ListenProvider; las burbujas arrancan
          debajo. Expandir vuelve al player POR `leave` (la charla termina igual
          al desmontar: así marca el capítulo como conversado, recuerda la
          sesión para "Seguir la charla" y vuelve por historial, en vez de
          apilar otro player); cerrar lo oculta y pausa el audio — en toda la
          app, igual que la X de la barra global (ver `ConversationListenBar`). */}
      <ConversationListenBar onExpand={leave} />

      {/* Examen de nivelación: progreso + cierre claro (analogía del tracker) */}
      {examMode && <ExamProgress messages={messages} onFinish={leave} />}

      {/* Badge de charla privada (visible toda la sesión mientras esté activa) */}
      {incognito && <PrivateBadge />}

      {/* Estado de conexión (reconectando / caída). El cierre server-side va
          aparte: SessionEndedCard al final del hilo de mensajes. */}
      <ConnectionBanner status={connectionStatus} onReconnect={reconnect} onLeave={leave} />

      {/* Modo juego (plan 47): score pill — nombre + Salir en todos los
          GAME_SEEDS, dificultad salvo describe-it, ronda / ✓ / racha en
          translate-it. En describe-it precede a la SecretWordCard. */}
      {gameArena.seed && (
        <GameModeChip
          seed={gameArena.seed}
          onExit={clearSeed}
          exitDisabled={ended}
          round={gameArena.round?.round ?? null}
          score={gameArena.score}
          scored={gameArena.scored}
          difficulty={gameArena.difficulty}
          band={gameArena.band}
          bandMoment={gameArena.bandMoment}
        />
      )}

      {/* Palabra secreta (juego describe-it): client-only, el coach no la ve. */}
      {activeSeed === 'describe-it' && secretWord && (
        <SecretWordCard word={secretWord} onReroll={rerollSecretWord} disabled={ended} />
      )}

      {/* Tracker de misión en vivo (sólo en escenarios curados con misión) */}
      {scenarioMission && (
        <MissionTracker
          mission={scenarioMission}
          completed={completedObjectives}
          justId={justObjectiveId}
          practiced={practicedObjectives}
          justPracticedId={justPracticedId}
        />
      )}

      {/* Tracker de SUB-UNIDAD en vivo (programa autorado, fase 17.B) */}
      {!scenarioMission && subUnit && (
        <UnitObjectivesTracker
          objectives={subUnitObjectives}
          completed={subUnitCompleted}
          justIdx={justSubObjIdx}
          justTick={subRepTick}
          reps={subUnitReps}
          counts={subUnitCounts}
          subUnitTitle={subUnit.titleEs}
          subUnitOrder={subUnit.order}
          subUnitTotal={subUnit.total}
          review={subUnitReview}
        />
      )}

      {/* Tracker de objetivos de la unidad en vivo (modo plan legacy, fase 16.B) */}
      {!scenarioMission && !subUnit && planObjectives.length > 0 && (
        <UnitObjectivesTracker
          objectives={planObjectives}
          completed={completedPlanObjectives}
          justIdx={justPlanObjectiveIdx}
        />
      )}

      {/* «Armá tu nave»: la pieza de esta charla y cuánto falta (sólo con el recorrido en curso). */}
      <ShipPieceTracker
        piece={shipPiece}
        live={conversationLive({
          scenarioMission,
          completedObjectives,
          subUnit,
          subUnitObjectives,
          subUnitCompleted,
          planObjectives,
          completedPlanObjectives,
        })}
        ask={{ ready: ctrl.shipCanAsk, onBack: ctrl.leaveToShip }}
        />

      {/* Cierre de la escena: FAB flotante (se ilumina al completar la misión).
          No obliga — el user puede seguir charlando o cerrar con la X. Se
          oculta con la card de Inspire abierta: caía encima de «Usar esto». */}
      {scenarioMission && (
        <MissionFinishFab
          allDone={allObjectivesDone}
          onFinish={leave}
          hidden={
            inspire.open ||
            assisted.cardVisible ||
            assisted.deckVisible ||
            !!ctrl.bonusPending ||
            !!ctrl.lessonCompletion
          }
        />
      )}

      {/* Cierre de la unidad (modo plan): mismo FAB, se ilumina al demostrar
          todos los objetivos en vivo. El dominio real lo decide el cierre.
          Con sub-unidades no hay FAB: la parte completa cierra la clase con
          `LessonCompletedCard` (final de clase, #450). */}
      {!scenarioMission && !subUnit && planObjectives.length > 0 && allPlanObjectivesDone && (
        <MissionFinishFab
          allDone
          onFinish={leave}
          hidden={inspire.open || assisted.cardVisible || assisted.deckVisible}
        />
      )}

      {/* Aviso de salida cuidada: charla demasiado corta para un debrief útil. */}
      {shortLeavePrompt && (
        <ShortLeaveSheet onStay={dismissLeaveShort} onLeave={confirmLeaveShort} />
      )}

      {/* Charla asistida: la hoja de instrucciones antes de la primera respuesta modelo. */}
      {assisted.phase === 'intro' && (
        <AssistedIntroSheet
          sceneTitle={assisted.sceneTitle}
          onStart={assisted.start}
          onSolo={assisted.goSolo}
        />
      )}

      {/* Drill modal (scoring real) */}
      {drillErr && (
        <DrillModal
          err={drillErr}
          onClose={() => setDrillErr(null)}
          {...(sessionId ? { sessionId } : {})}
          pauseMainCapture={pauseMainCapture}
          resumeMainCapture={resumeMainCapture}
        />
      )}

      {/* Messages */}
      <div
        className={`app-scroll ${assisted.deckVisible ? 'conv-assisted' : ''}`}
        ref={threadRef}
        role="log"
        aria-label="Mensajes de la conversación"
        onScroll={handleThreadScroll}
        style={{ padding: '20px 18px 12px', display: 'flex', flexDirection: 'column', gap: 'var(--msg-gap, 22px)' }}
      >
        <ConversationMessages ctrl={ctrl} />
        {/* El coach no pudo responder (aviso del server o tope de 20 s): debajo
            del mensaje que quedó sin respuesta. Un cierre de la charla la tapa. */}
        {serverEnded == null && replyFailure != null && !ctrl.lessonCompletion ? (
          <ReplyFailedCard kind={replyFailure} onRetry={retryFailedReply} onLeave={leave} />
        ) : null}
        {/* Cierre server-side (idle): countdown de preaviso → card terminal,
            ambos al FINAL del hilo. */}
        {serverEnded != null && serverEnded !== 'lesson-completed' ? (
          <SessionEndedCard reason={serverEnded} onResume={resumeConversation} onLeave={leave} />
        ) : serverClosing ? (
          <SessionClosingCard inSeconds={serverClosing.inSeconds} />
        ) : null}
        {/* Charla asistida: la hoja va al final de la charla, en lugar del
            composer (Emmanuel eligió la maqueta 1 · Escenario con la hoja de la
            2, 16/10). Al subir se apaga; al volver abajo aparece. */}
        {assisted.deckVisible && (
          <AssistedCard
            assisted={assisted}
            voiceId={coach.activeVoiceId ?? FALLBACK_VOICE_ID}
            onDuck={freeChat.setDucked}
            onFinish={leave}
            recorder={recorder}
            holdDisabled={holdDisabled}
          />
        )}
        {/* Autoscroll respetuoso: con el user scrolleado arriba el streaming
            no lo arrastra — este chip sticky lo devuelve al final. */}
        {threadUnpinned && <JumpToLatestChip onJump={jumpToLatest} />}
      </div>

      {/* Card de sugerencias del Inspire (flota arriba del footer). */}
      {/* Charla asistida, después de «Hacelo solo»: la tarjeta «Ahora solo». */}
      {assisted.phase === 'off' && assisted.soloSince !== null && (
        <AssistedCard assisted={assisted} voiceId={coach.activeVoiceId ?? FALLBACK_VOICE_ID} />
      )}
      {inspire.open && (
        <InspireCard
          suggestions={inspire.suggestions}
          idx={inspire.idx}
          loading={inspire.loading}
          error={inspire.error}
          voiceId={coach.activeVoiceId ?? FALLBACK_VOICE_ID}
          onCycle={inspire.cycle}
          onUse={(sug) => sendInspireSuggestion(sug.en)}
          readAloud={inspireReadAloud}
          onRetry={inspire.retry}
          onClose={inspire.close}
        />
      )}

      {/* Hint sutil: las palabras del coach son clickeables (descartable, 24 h).
          Se oculta si la card de sugerencias está abierta para no solaparse,
          — and, in English: hidden too during a translate-it game (the arena is busy enough). */}
      <TapWordHint
        hidden={inspire.open || assisted.cardVisible || assisted.deckVisible || gameArena.round != null}
      />

      {/* Red A0 (plan 33 §1.1): chips tap-or-speak del objetivo pendiente. Sólo
          en charla a0 con misión; el hook se monta SIEMPRE (gate de RENDER acá,
          no de montaje — anti "Rendered more hooks"). */}
      {a0Mission && a0SuggestionStep && (
        <A0Suggestions
          step={a0SuggestionStep}
          onSend={sendA0Suggestion}
          busy={state === 'thinking' || state === 'speaking' || ended}
        />
      )}

      {/* Composer compartido (@orborix/voice-composer): campo + mic hold, con
          "+" de Actividades a la izquierda y la lamparita de Inspire inline
          dentro del campo. El menú (sheet) se renderiza aparte, abajo. */}
      {/* En la charla asistida el micrófono es el de la hoja (mismo recorder). */}
      {!assisted.deckVisible && (
        <div className="mic-bar">
          <VoiceComposer
            recorder={recorder}
            holdDisabled={holdDisabled}
            micHidden={micHidden}
            disabled={ended || assistedPaused}
            placeholder={
              ended
                ? COMPOSER_PLACEHOLDER_ENDED
                : micHidden
                  ? COMPOSER_PLACEHOLDER_TEXT_ONLY
                  : COMPOSER_PLACEHOLDER
            }
            onSendText={sendComposerText}
            labels={COMPOSER_LABELS}
            freeChat={freeChat}
            freeStatus={freeStatus}
            turnClosedTick={ctrl.userTurnTick}
            leading={
              // "+" = Actividades: mismo estado/gating que el viejo pill del
              // header (sólo en tu turno; oculto fuera de charla libre pura).
              !seedDisabledReason ? (
                <button
                  type="button"
                  className={`conv-plus-btn ${activeSeed ? 'is-active' : ''}`}
                  disabled={!isUserTurn}
                  onClick={() => {
                    inspire.close();
                    openSeedMenu();
                  }}
                  aria-haspopup="dialog"
                  aria-expanded={seedMenuOpen}
                  aria-label="Actividades"
                  title="Actividades"
                >
                  <IconPlus size={20} />
                </button>
              ) : undefined
            }
            fieldStart={
              // Bombita de Inspire: habilitada sólo en tu turno; si la card ya
              // está abierta sigue clickeable para poder cerrarla.
              <InspireButton
                onClick={inspire.toggleOpen}
                active={inspire.open}
                disabled={ended || assisted.phase !== 'off' || (!inspire.open && !isUserTurn)}
              />
            }
          />
        </div>
      )}

      {/* Menú de temas (¿De qué hablamos?) */}
      {seedMenuOpen && (
        <DirectionMenu
          variant="sheet"
          activeSeed={activeSeed}
          onSelectSeed={selectSeed}
          onScenario={goToScenarios}
          onNews={selectNews}
          onClear={clearSeed}
          onClose={closeSeedMenu}
        />
      )}

      {/* Settings sheet */}
      {settingsOpen && (
        <SettingsSheet
          settings={settings}
          onChange={setSettings}
          onIntensityChange={setGlobalIntensity}
          onClose={() => setSettingsOpen(false)}
          coach={coach}
          incognito={incognito}
          onToggleIncognito={toggleIncognito}
        />
      )}
    </div>
  );
}
